/**
 * Venue management for Wolt settlement upload.
 * Matches venue names from the Wolt report against venues.wolt_venue_name.
 */
import { supabase } from '../../../lib/supabase';

/**
 * Get all venues with their wolt_venue_name and department info.
 */
export const getVenuesForWolt = async () => {
  try {
    const { data, error } = await supabase
      .from('venues')
      .select('id, wolt_venue_name, department_name')
      .order('wolt_venue_name');

    if (error) {
      throw new Error(error.message);
    }

    return { success: true, venues: data || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

/**
 * Get venues that have no wolt_venue_name set (for matching new venue names).
 */
export const getVenuesWithoutWoltName = async () => {
  try {
    const { data, error } = await supabase
      .from('venues')
      .select('id, wolt_venue_name, department_name')
      .order('department_name');

    if (error) {
      throw new Error(error.message);
    }

    const venues = (data || []).filter(
      (v) => v.wolt_venue_name == null || String(v.wolt_venue_name).trim() === ''
    );
    return { success: true, venues };
  } catch (err) {
    return { success: false, error: err.message };
  }
};

/**
 * Set wolt_venue_name on a venue (for matching a report venue name to a department).
 * @param {number|string} venueId - venue id
 * @param {string} woltVenueName - the venue name as it appears in the Wolt report
 */
export const updateVenueWoltName = async (venueId, woltVenueName) => {
  try {
    if (!venueId || !woltVenueName) {
      throw new Error('Venue ID and Wolt venue name are required');
    }

    const numericVenueId = typeof venueId === 'string' ? parseInt(venueId, 10) : venueId;
    if (isNaN(numericVenueId)) {
      throw new Error('Invalid venue id');
    }

    const { data, error } = await supabase
      .from('venues')
      .update({ wolt_venue_name: woltVenueName.trim() })
      .eq('id', numericVenueId)
      .select('id, wolt_venue_name, department_name')
      .single();

    if (error) {
      throw new Error(`Failed to update venue: ${error.message}`);
    }

    return { success: true, venue: data };
  } catch (err) {
    return { success: false, error: err.message };
  }
};
